import { BUCKET_CONSTANTS, DEFAULT_NUMERIC_VALUES } from "../constants/NumericConstants";

export enum KeyPressEventKeys {
  "arrowDown" = "ArrowDown",
  "arrowUp" = "ArrowUp",
  "enter" = "Enter",
  "esc" = "Esc",
  "tab" = "Tab",
}

export enum GlobalSearchCategories {
  "all" = "all",
  "activity_streams" = "activity_streams",
  "connections" = "connections",
  "contacts" = "contacts",
  "attachments" = "attachments",
  "invoices" = "invoices",
  "bills" = "invoices",
  "payments" = "payments",
  "sent_einvoices" = "sent_einvoices",
  "received_einvoices" = "received_einvoices",
}

export enum StatusBackendNumberMap {
  "active" = "0",
  "open" = "0",
  "closed" = "1",
  "resolved" = "1",
  "waiting_for_response" = "2",
  "pending" = "2",
  "snoozed" = "3",
}

export enum DataMode {
  View,
  Edit,
}

export enum InvoiceStatusType {
  "OPEN" = BUCKET_CONSTANTS.OPEN,
  "PAST DUE 1-30" = BUCKET_CONSTANTS.FIRST_PAST_DUE_BUCKET,
  "PAST DUE 31-60" = BUCKET_CONSTANTS.SECOND_PAST_DUE_BUCKET,
  "PAST DUE 61-90" = BUCKET_CONSTANTS.THIRD_PAST_DUE_BUCKET,
  "PAST DUE 91+" = BUCKET_CONSTANTS.FOURTH_PAST_DUE_BUCKET,
  "CLOSED" = BUCKET_CONSTANTS.CLOSED,
}

export enum InvoiceStatusAging {
  "FUTURE DUE" = "Future Due",
  "LATE 1-30 DAYS" = "1+ Late",
  "LATE 31-60 DAYS" = "31+ Late",
  "LATE 61-90 DAYS" = "61+ Late",
  "LATE 91+ DAYS" = "91+ Late",
}

export enum EntityClassification {
  corporation = "Corporation",
  partnership = "Partnership",
  "sole-proprietor" = "Sole-proprietor",
}

/**
 * Contains the possible roles that a user can have.
 */
export enum UserRoles {
  GroupOwner = "Group Owner",
  GroupAdmin = "Group Admin",
  Member = "Member",
  ReadOnly = "Read-Only",
}

/**
 * Holds all actions that will be only allowed for some users
 */
export enum UserActions {
  AddNote = "addActivity",
  InvitationsRemind = "invRemind",
  InvitationsRemove = "invRemove",
  InvitationsCheckbox = "invCheckbox",
  RemovedMembersInvite = "rmvedMembersInv",
  RemovedMembersCheckbox = "rmvCheckbox",
  InviteMembers = "invMembers",
  InvitationsChangeRole = "invChangeRoles",
  ActiveMembersCheckbox = "actvCheckbox",
  ActiveMembersRemove = "actvRemove",
  ChangeRoles = "changeRoles",
  EditMyProfile = "editMyProfile",
  EditMyCompany = "editMyCompany",
  AddEmailClient = "addEmailClient",
  RunSync = "runSync",
  EditAccountingSoftware = "editAccountingSoftware",
  EditWorkspace = "editWorkspace",
}

/**
 * The possible statuses an activity can have.
 */
export enum ActivityStatus {
  Active = "Active",
  Closed = "Closed",
  NotStarted = "Not Started",
  WaitingForResponse = "Waiting_For_Response",
  Spam = "Spam",
  Snoozed = "Snoozed",
}

/**
 * The possible statuses an activity can have.
 */
export enum ActivityActionTypes {
  Status = "Status",
  Read = "Read",
  Spam = "Spam",
  Assign = "Assign",
  Unassign = "Unassign",
  Move = "Move",
  Fraud = "Fraud",
}

/**
 * Types of fallback
 */
export enum FallbackTypes {
  Id = DEFAULT_NUMERIC_VALUES.DEFAULT_NEG_ONE,
}

/**
 * Types of activities in a Activity Feed
 */
export enum ActivityType {
  Note = "note",
  Move = "move",
  PhoneCall = "call_log",
  Email = "input_email",
  IncomingEmail = "incoming_email",
  BouncedEmail = "bounced_email",
  AuditLog = "audit_log",
  Approval = "approval",
  Forward = "forward", // if stream item is input_email but has original_activity_id
  ApprovalRedirect = "approval_redirect",
  ApprovalRequest = "approval_request",
  ApprovalReviewed = "approval_reviewed",
  ApprovalResponse = "approval_response",
  ApprovalAccessRevoked = "access_revoked",
  ApprovalCancelled = "approval_cancelled",
  ApprovalLinkRegenerated = "regenerated_approval",
  ForwardApAutomation = "ap_automation",
  Nudge = "nudge",
  APAutomation = "ap_automation",
  VendorOnboarding = "started_vendor_onboarding",
  AP_AR_SHARE_PROFILE = "profile_share",
  AR_REQUEST_PROFILE = "profile_request",
}

export enum EmailType {
  bounce = "bounce",
  standard = "standard",
  outOfOffice = "out_of_office",
  nudge = "nudge",
}

export enum ActivityOptions {
  PHONE_CALL = "phone_call",
  NOTE = "note",
  APPROVAL_REQUEST = "approval_request",
  EMAIL = "email",
  PAYMENT_REMINDER = "payment_reminder",
  ONBOARD_VENDOR = "new_vendor_onboarding",
  SHARE_AP_PROFILE = "share_ap_profile",
  SHARE_AR_PROFILE = "share_ar_profile",
  REQUEST_CUSTOMER_PROFILE = "request_customer_profile",
}

export enum AuditLogType {
  Assign = "assign",
  Unassign = "unassign",
  Close = "closed",
  Spam = "spam",
  Unspam = "unspam",
  Forward = "forward",
  Snoozed = "snoozed",
  Unsnoozed = "unsnoozed",
  SpamConnection = "connection_spam",
  UnspamConnection = "connection_unspam",
  FraudConnection = "connection_fraud",
  UnfraudConnection = "connection_unfraud",
  Move = "move",
  MergeConnection = "connection_merge",
  OnboardRequest = "onboard_request",
  ApAutomation = "ap_automation",
  SaveApAutomationEmail = "save_automation_email",
  VendorOnboardingCompleted = "vendor_onboarding_completed",
  ProfileShare = "profile_share",
  ProfileRequest = "profile_request",
}

export enum InlinePrompts {
  ONBOARD_VENDOR = "onboard_vendor",
}

export enum TopCompaniesBy {
  Recent_Activity = "Recent Activity",
  Past_Due_Balance = "Past Due Balance",
  Past_Due_Invoices = "Past Due Invoices",
  Total_Balance = "Total Balance",
  Open_Invoices = "Open Invoices",
  Coming_Due = "Coming Due",
  Open_Bills = "Open Bills",
}

/**
 * Types of Searchlight Date Types most commonly used with our Table Component
 */
export enum SearchlightDataTypes {
  String = "String",
  Number = "Number",
  Date = "Date", // "YYYY-MM-DD"
  DateTime = "DateTime", // "YYYY-MM-DDTHH:MM:SS"
}
/**
 * Types of Searchlight Tokens most commonly used with our Table Component
 */
export enum SearchlightTokens {
  EQ = "EQ",
  GT = "GT",
  GE = "GE",
  NE = "NE",
  LT = "LT",
  LE = "LE",
  STARTSWITH = "STARTSWITH",
  CONTAINS = "CONTAINS",
  ENDSWITH = "ENDSWITH",
}

export enum EmailConnectorStatus {
  disconnected = "disconnected",
  active = "active",
}

export enum ChannelAction {
  connectToWorkspace = "connect_to_workspace",
  createWorkspace = "create_workspace",
  reconnect = "reconnect",
}

export enum ConnectorName {
  "Microsoft Outlook" = "microsoft_office",
  "Gmail" = "gmail",
}

/**
 * Mapping from Searchlight Tokens to Human Readable text
 */
export const SearchlightTokenToHumanReadableTextMap = new Map([
  [SearchlightTokens.EQ, "Equals"],
  [SearchlightTokens.GT, "Greater Than"],
  [SearchlightTokens.GE, "Greater Than or Equal To"],
  [SearchlightTokens.NE, "Not Equal To"],
  [SearchlightTokens.LT, "Less Than"],
  [SearchlightTokens.LE, "Less Than or Equal To"],
  [SearchlightTokens.STARTSWITH, "Starts With"],
  [SearchlightTokens.CONTAINS, "Contains"],
  [SearchlightTokens.ENDSWITH, "Ends With"],
]);

/**
 * Mapping from Searchlight Data Types to Searchlight Tokens acceptable with Type
 */
export const SearchlightDataTypeToTokenMap = new Map([
  [
    SearchlightDataTypes.String,
    [
      SearchlightTokens.EQ,
      SearchlightTokens.GT,
      SearchlightTokens.GE,
      SearchlightTokens.NE,
      SearchlightTokens.LT,
      SearchlightTokens.LE,
      SearchlightTokens.STARTSWITH,
      SearchlightTokens.CONTAINS,
      SearchlightTokens.ENDSWITH,
    ],
  ],
  [
    SearchlightDataTypes.Number,
    [SearchlightTokens.EQ, SearchlightTokens.GT, SearchlightTokens.GE, SearchlightTokens.NE, SearchlightTokens.LT, SearchlightTokens.LE],
  ],
  [
    SearchlightDataTypes.Date,
    [SearchlightTokens.EQ, SearchlightTokens.GT, SearchlightTokens.GE, SearchlightTokens.NE, SearchlightTokens.LT, SearchlightTokens.LE],
  ],
  [
    SearchlightDataTypes.DateTime,
    [SearchlightTokens.EQ, SearchlightTokens.GT, SearchlightTokens.GE, SearchlightTokens.NE, SearchlightTokens.LT, SearchlightTokens.LE],
  ],
]);

/**
 * Searchlight Data Type to Regex Validation object used for Input Validation
 */
export const SearchlightDataTypeToRegexValidation = new Map([
  [SearchlightDataTypes.String, /.*/],
  [SearchlightDataTypes.Number, /^[+-]?(\d+\.?\d*|\.\d+)$/],
  [SearchlightDataTypes.Date, /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12]\d|3[01])$/],
  [SearchlightDataTypes.DateTime, /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?$/],
]);

export enum AdvancedQueryFilter {
  TEXT = "0",
  INPUT_TEXT = "1",
  INPUT_TEXTAREA = "2",
  INPUT_EMAIL = "3",
  INPUT_SELECT = "4",
  CARDS = "5",
  FORM = "6",
  ARTICLE = "7",
  INCOMING_EMAIL = "8",
  INPUT_CSAT = "9",
  NOTE = "10",
  CALL_LOG = "11",
}

export enum EmailAction {
  NEW = "new",
  REPLY = "reply",
  REPLY_ALL = "reply_all",
  FORWARD = "forward",
  VIEW_ORIGINAL = "view_original",
  NUDGE = "nudge",
  FORWARD_AP_AUTOMATION = "forward_ap_automation",
  SHARE_AP_PROFILE = "share_ap_profile",
  SHARE_AR_PROFILE = "share_ar_profile",

  SEND_FOR_APPROVAL = "send_for_approval",
  MARK_SENDER_AS_SPAM = "mark_sender_as_spam",
  MARK_SENDER_AS_FRAUD = "mark_sender_as_fraud",
}

export enum ConnectionStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  FRAUD = "fraud",
  SPAM = "spam",
}

export enum ContactType {
  "EMAIL" = "Email",
  "ACCOUNTING_SYSTEM" = "Accounting System",
}

export enum WorkspaceType {
  AR = "ar",
  AP = "ap",
  AW = "aw",
}

/**
 * Tracking Event Names
 */
export enum TrackEventType {
  EMAIL_ACTIVITY = "Email Activity",
  NOTE = "Note",
  PHONE_CALL = "Phone Call",
  APPROVAL_REQUEST = "Approval Request",
}

export enum ActivityTabType {
  Profile,
  Transactions,
  Attachments,
}

export enum TransactionType {
  Invoice = "invoice",
  Payment = "payment",
}

export enum AttachmentType {
  INVOICE = "invoice",
  PAYMENT = "payment",
  DOCUMENT = "document",
  BILL = "bill",
}

export enum FilterType {
  Search = "search",
  Select = "select",
}

export enum Position {
  Left = "left",
  Right = "right",
  Center = "center",
}

export enum FilterToken {
  Equal = "eq",
  NotEqual = "ne",
  LessThan = "lt",
  GreaterThan = "gt",
  LessThanOrEqual = "le",
  GreaterThanOrEqual = "ge",
  ContainsAny = "cont_any",
  Contains = "cont",
}

export enum Order {
  DESC = "desc",
  ASC = "asc",
}

export enum APARStances {
  SHARE_AP_PROFILE = "Share AP Profile",
  SHARE_AR_PROFILE = "Share AR Profile",
  REQUEST_CUSTOMER_PROFILE = "Request Customer Profile",
}

export enum EmojiActionIds {
  selector = "selector",
  tag = "tag",
}

export enum EmojiOptions {
  THUMBS_UP = "thumbs_up",
  THUMBS_DOWN = "thumbs_down",
  HEART = "heart",
  HAPPY = "happy",
  SAD = "sad",
  CELEBRATE = "celebrate",
}

export enum KeyBoardActionHelperText {
  TO_SELECT = "To Select",
  TO_DISMISS = "To Dismiss",
  TO_NAVIGATE = "To Navigate",
  TO_MOVE_BETWEEN_SECTIONS = "To Move Between Sections",
}

export enum CompanyType {
  VENDOR = "Vendor",
  CUSTOMER = "Customer",
  OTHER_CONNECTION = "Other Connection",
  MY_COMPANY = "My Company",
}

export enum WorkspaceRouteId {
  accounts_payable = "ap",
  accounts_receivable = "ar",
  accounting = "aw",
}

export enum ACCOUNT_ID {
  QBO = "00000000-0000-0000-0000-000000000001",
  Xero = "00000000-0000-0000-0000-000000000002",
  Intacct = "00000000-0000-0000-0000-000000000004",
  Demo = "00000000-0000-0000-0002-000000000001",
  Sage50 = "00000000-0000-0000-0004-000000000001",
  Sage100 = "00000000-0000-0000-0004-000000000002",
}

export enum ERP_APP_TYPE {
  ERP_CONNECTOR = "ERP Connector",
  ON_PREM_ERP_CONNECTOR = "On-Prem ERP Connector",
}

export enum VIEW_CATEGORIES {
  //Activities
  ALL_ACTIVITES = "all-activities",
  MINE_ACTIVITES = "mine-activities",
  COMPLETED_ACTIVITES = "completed-activities",
  AWAITING_ACTIVITES = "awaiting-activities",
  SNOOZED_ACTIVITES = "snoozed-activities",
  UNASSIGNED_ACTIVITES = "unassigned-activities",
  SPAM_ACTIVITES = "spam-activities",
  CUSTOMER_ACTIVITES = "customer-activities",
  VENDOR_ACTIVITES = "vendor-activities",
  INTERNAL_ACTIVITES = "internal-activities",
  CONNECTION_ACTIVITES = "connection-activities",
  ACTIVE_CUSTOMER_ACTIVITES = "active-customer_activities",
  ARCHIVED_CUSTOMER_ACTIVITES = "archived-customer_activities",
  SPAM_CUSTOMER_ACTIVITES = "spam-customer_activities",
  FRAUD_CUSTOMER_ACTIVITES = "fraud-customer_activities",
  ACTIVE_VENDOR_ACTIVITES = "active-vendor_activities",
  ARCHIVED_VENDOR_ACTIVITES = "archived-vendor_activities",
  SPAM_VENDOR_ACTIVITES = "spam-vendor_activities",
  FRAUD_VENDOR_ACTIVITES = "fraud-vendor_activities",
  ACTIVE_OTHER_ACTIVITES = "active-other-connection_activities",
  ARCHIVED_OTHER_ACTIVITES = "archived-other-connection_activities",
  SPAM_OTHER_ACTIVITES = "spam-other-connection_activities",
  FRAUD_OTHER_ACTIVITES = "fraud-other-connection_activities",
  ACTIVE_INTERNAL_ACTIVITES = "active-internal_activities",
  ARCHIVED_INTERNAL_ACTIVITES = "archived-internal_activities",

  //Transactions
  INVOICES = "invoice",
  INVOICES_1 = "invoice-1",
  INVOICES_31 = "invoice-31",
  INVOICES_61 = "invoice-61",
  INVOICES_91 = "invoice-91",
  INVOICES_FUTURE = "invoices-future",
  INVOICES_CLOSED = "invoices-closed",
  BILLS = "bills",
  BILLS_1 = "bills-1",
  BILLS_31 = "bills-31",
  BILLS_61 = "bills-61",
  BILLS_91 = "bills-91",
  BILLS_FUTURE = "bills-future",
  BILLS_CLOSED = "bills-closed",
  VENDOR_PAYMENTS = "vendor-payments",
  VENDOR_ADVANCE_PAYMENTS = "vendor-advance-payments",
  CUSTOMER_PAYMENTS = "customer-payments",
  CUSTOMER_UNAPPLIED_PAYMENTS = "customer-unapplied-payments",

  //Connections
  ACTIVE_CUSTOMER = "active-customer",
  ARCHIVED_CUSTOMER = "archived-customer",
  SPAM_CUSTOMER = "spam-customer",
  FRAUD_CUSTOMER = "fraud-customer",
  ACTIVE_VENDOR = "active-vendor",
  ARCHIVED_VENDOR = "archived-vendor",
  SPAM_VENDOR = "spam-vendor",
  FRAUD_VENDOR = "fraud-vendor",
  ACTIVE_OTHER = "active-other-connection",
  ARCHIVED_OTHER = "archived-other-connection",
  SPAM_OTHER = "spam-other-connection",
  FRAUD_OTHER = "fraud-other-connection",
  ACTIVE_INTERNAL = "active-internal",
  ARCHIVED_INTERNAL = "archived-internal",

  //Connnection details
  ACTIVE_CUSTOMER_OPEN_INVOICES = "active-customer-open-invoices",
  ARCHIVED_CUSTOMER_OPEN_INVOICES = "archived-customer-open-invoices",
  SPAM_CUSTOMER_OPEN_INVOICES = "spam-customer-open-invoices",
  FRAUD_CUSTOMER_OPEN_INVOICES = "fraud-customer-open-invoices",
  ACTIVE_VENDOR_OPEN_BILLS = "active-vendor-open-bills",
  ARCHIVED_VENDOR_OPEN_BILLS = "archived-vendor-open-bills",
  SPAM_VENDOR_OPEN_BILSS = "spam-vendor-open-bills",
  FRAUD_VENDOR_OPEN_BILLS = "fraud-vendor-open-bills",
  ACTIVE_CUSTOMER_CLOSED_INVOICES = "active-customer-closed-invoices",
  ARCHIVED_CUSTOMER_CLOSED_INVOICES = "archived-customer-closed-invoices",
  SPAM_CUSTOMER_CLOSED_INVOICES = "spam-customer-closed-invoices",
  FRAUD_CUSTOMER_CLOSED_INVOICES = "fraud-customer-closed-invoices",
  ACTIVE_VENDOR_CLOSED_BILLS = "active-vendor-closed-bills",
  ARCHIVED_VENDOR_CLOSED_BILLS = "archived-vendor-closed-bills",
  SPAM_VENDOR_CLOSED_BILLS = "spam-vendor-closed-bills",
  FRAUD_VENDOR_CLOSED_BILLS = "fraud-vendor-closed-bills",
  ACTIVE_CUSTOMER_PAYMENTS = "active-customer-payments",
  ARCHIVED_CUSTOMER_PAYMENTS = "archived-customer-payments",
  SPAM_CUSTOMER_PAYMENTS = "spam-customer-payments",
  FRAUD_CUSTOMER_PAYMENTS = "fraud-customer-payments",
  ACTIVE_VENDOR_PAYMENTS = "active-vendor-payments",
  ARCHIVED_VENDOR_PAYMENTS = "archived-vendor-payments",
  SPAM_VENDOR_PAYMENTS = "spam-vendor-payments",
  FRAUD_VENDOR_PAYMENTS = "fraud-vendor-payments",
  ACTIVE_CUSTOMER_CONTACTS = "active-customer-contacts",
  ARCHIVED_CUSTOMER_CONTACTS = "archived-customer-contacts",
  SPAM_CUSTOMER_CONTACTS = "spam-customer-contacts",
  FRAUD_CUSTOMER_CONTACTS = "fraud-customer-contacts",
  ACTIVE_VENDOR_CONTACTS = "active-vendor-contacts",
  ARCHIVED_VENDOR_CONTACTS = "archived-vendor-contacts",
  SPAM_VENDOR_CONTACTS = "spam-vendor-contacts",
  FRAUD_VENDOR_CONTACTS = "fraud-vendor-contacts",
  ACTIVE_OTHER_CONTACTS = "active-other-connection-contacts",
  ARCHIVED_OTHER_CONTACTS = "archived-other-connection-contacts",
  SPAM_OTHER_OPEN_CONTACTS = "spam-other-connection-contacts",
  FRAUD_OTHER_OPEN_CONTACTS = "fraud-other-connectio-contacts",
  ACTIVE_CUSTOMER_DOCUMENTS = "active-customer-documents",
  ARCHIVED_CUSTOMER_DOCUMENTS = "archived-customer-documents",
  SPAM_CUSTOMER_DOCUMENTS = "spam-customer-documents",
  FRAUD_CUSTOMER_DOCUMENTS = "fraud-customer-documents",
  ACTIVE_VENDOR_DOCUMENTS = "active-vendor-documents",
  ARCHIVED_VENDOR_DOCUMENTS = "archived-vendor-documents",
  SPAM_VENDOR_DOCUMENTS = "spam-vendor-documents",
  FRAUD_VENDOR_DOCUMENTS = "fraud-vendor-documents",
  ACTIVE_OTHER_DOCUMENTS = "active-other-connection-documents",
  ARCHIVED_OTHER_DOCUMENTS = "archived-other-connection-documents",
  SPAM_OTHER_OPEN_DOCUMENTS = "spam-other-connection-documents",
  FRAUD_OTHER_OPEN_DOCUMENTS = "fraud-other-connectio-documents",
  ACTIVE_INTERNAL_DOCUMENTS = "active-internal-documents",
  ARCHIVED_INTERNAL_DOCUMENTS = "archived-internal-documents",
}

export enum EXPORT_CATEGORIES {
  AP_INVOICES = "ap_invoices",
  AR_INVOICES = "ar_invoices",
  AP_PAYMENTS = "ap_payments",
  AR_PAYMENTS = "ar_payments",
  UNAPPLIED_PAYMENTS = "unapplied_payments",
  ADVANCE_PAYMENTS = "advance_payments",
  CUSTOMER = "customers",
  VENDOR = "vendors",
  OTHER_CONNECTION = "non_erp_connections",
  MY_COMPANY = "internal_connections",
}

export enum EINVOICE_ACTIONS {
  IN_HAND = "in_hand",
  REFUSE = "refuse",
  APPROVE = "approve",
  PARTIALLY_APPROVE = "partially_approve",
  SUSPEND = "suspend",
  COMPLETE = "complete",
  PAYMENT_SENT = "payment_sent",
  PAYMENT_RECEIVED = "payment_received",
  DISPUTE = "dispute",
}

export enum EINVOICE_STATES {
  IN_HAND = "in_hand",
  REFUSED = "refused",
  APPROVED = "approved",
  PARTIALLY_APPROVED = "partially_approved",
  SUSPENDED = "suspended",
  COMPLETED = "completed",
  PAYMENT_SENT = "payment_sent",
  PAYMENT_RECEIVED = "payment_received",
  DISPUTED = "disputed",
}

export enum EINVOICE_ATTACHMENT_TYPES {
  DEMATERIALISED = "ObjectDematerializedInvoice",
  OBJECT_PDF = "ObjectPDF",
}
